import { Audit } from '@/models/registry';
import type { Audit as AuditType } from '@/models/types';
import { makeAutoObservable } from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import type { BaseStore } from './base';

export class AuditStore implements BaseStore<Audit, AuditType> {
  store: Map<string, Audit> = new Map();

  storeIndexedByForeignKey: Map<string, Audit[]> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<AuditType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  private setAuditField = <K extends keyof AuditType>(
    audit: Audit,
    key: K,
    value: AuditType[K],
  ) => {
    (audit as Pick<Audit, K>)[key] = value as Audit[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<AuditType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.deal_audits.delete(delta.record_id);
      }
      this.remove({ id: delta.record_id, dealId: delta.data.deal_id });
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.deal_audits.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.deal_audits.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.deal_audits.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({ delta }: { delta: DeltaPacketRecord<AuditType> }) {
    const existingComment = this.getById(delta.record_id);
    if (!existingComment) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof AuditType,
      AuditType[keyof AuditType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingComment[key] === newValue) {
        continue;
      }

      this.setAuditField(existingComment, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingComment);
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<AuditType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.deal_audits.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.remove({ id: delta.record_id, dealId: delta.data.deal_id });
      return;
    }

    if (delta.action === 'archive') {
      this.remove({ id: delta.record_id, dealId: delta.data.deal_id });
      return;
    }

    this.upsert(delta.data);
  }

  getById(id: string): Audit | undefined {
    return this.store.get(id);
  }

  add(input: AuditType): void {
    const audit = new Audit(input);
    this.store.set(audit.id, audit);
    // Index by foreign key for O(1) lookups
    const arr = this.storeIndexedByForeignKey.get(audit.deal_id) || [];
    arr.push(audit);
    this.storeIndexedByForeignKey.set(audit.deal_id, arr);
  }

  save(audit: Audit) {
    this.store.set(audit.id, audit);
    // Index by foreign key for O(1) lookups
    const arr = this.storeIndexedByForeignKey.get(audit.deal_id) || [];
    const index = arr.findIndex((a) => a.id === audit.id);
    if (index !== -1) {
      arr[index] = audit;
    }
    this.storeIndexedByForeignKey.set(audit.deal_id, arr);
  }

  upsert(audit: AuditType) {
    const existingAudit = this.getById(audit.id);
    if (existingAudit) {
      this.replace({ id: audit.id, audit });
    } else {
      this.add(audit);
    }
  }

  replace({ id, audit }: { id: string; audit: AuditType }) {
    const existingAudit = this.getById(id);
    if (!existingAudit) return;

    existingAudit.old_value = audit.old_value;
    existingAudit.new_value = audit.new_value;
    existingAudit.message = audit.message;

    this.save(existingAudit);
  }

  remove({ id, dealId }: { id: AuditType['id']; dealId: string }) {
    this.store.delete(id);

    // remove from the index
    const arr = this.storeIndexedByForeignKey.get(dealId) || [];
    this.storeIndexedByForeignKey.set(
      dealId,
      arr.filter((a) => a.id !== id),
    );
  }

  hydrateLocal(): Promise<void> {
    return hydrateFromWorker({
      tableName: 'deal_audits',
      entity: 'deal_audits',
      modelStore: auditStore,
    });
  }

  async hydrateDeal(dealId: AuditType['deal_id']): Promise<void> {
    const audits = await db.deal_audits.toArray();
    audits.forEach((audit) => {
      if (audit.deal_id === dealId) this.upsert(audit);
    });
  }
}

export const auditStore = new AuditStore();
